import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, MapPin, Navigation, Loader2, AlertTriangle, Building, Map, X } from 'lucide-react';
import { parseInput } from '../../utils/coordinateUtils';
import { geocodeAddress, searchBuildings } from '../../services/apiService';

const CoverageSearch = ({ onSearch, onSearchStart, isLoading, externalInput }) => {
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [isGeocoding, setIsGeocoding] = useState(false);

    // UI State for Modal and Selection
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [pendingSearchValue, setPendingSearchValue] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [selectedLocationType, setSelectedLocationType] = useState('street');
    // Only the newest lookup is allowed to update the UI. Without this guard, a
    // slow OzMap response from an earlier condominium search can arrive later
    // and replace the results of the current search.
    const activeSearchRef = useRef(0);

    useEffect(() => {
        if (externalInput) {
            setInput(externalInput);
        }
    }, [externalInput]);

    const performSearch = async (searchValue, type) => {
        const searchId = ++activeSearchRef.current;
        setIsGeocoding(true);
        setError(null);
        setWarning(null);
        setSearchResults([]);
        setShowSelectionModal(false);
        setSelectedLocationType(type);

        try {
            let response = null;

            if (type === 'building' || type === 'condo') {
                // Search ONLY for buildings/condos in Ozmap
                response = await searchBuildings(searchValue);

                // The server echoes the query that produced the response. This
                // makes a stale/intermediary response detectable instead of
                // ever presenting it as the result of the current input.
                if ((response?.query || '').trim().toLocaleLowerCase('pt-BR') !== searchValue.trim().toLocaleLowerCase('pt-BR')) {
                    throw new Error('A resposta recebida não pertence à consulta atual.');
                }
            } else {
                // Search ONLY for street addresses via Geocoding
                response = await geocodeAddress(searchValue);
            }

            if (searchId !== activeSearchRef.current) return;

            if (response && response.success && response.data && response.data.length > 0) {
                // Check source for warning
                const firstResult = response.data[0];
                if (firstResult.source === 'nominatim') {
                    setWarning('Atenção: Busca aproximada (Google Maps indisponível). Verifique a posição no mapa.');
                } else if (firstResult.source === 'ozmap_building') {
                    setWarning('Condomínio/Prédio encontrado no sistema. Clique em verificar para confirmar a viabilidade.');
                }

                if (response.data.length === 1 && !response.data[0].approximate) {
                     const { lat, lng, formatted_address, source } = response.data[0];
                     const resolvedType = source === 'ozmap_region' ? 'condo' : type;
                     const coverageSearchText = resolvedType === 'street' ? (formatted_address || searchValue) : searchValue;
                     onSearch({ lat, lng }, null, coverageSearchText, resolvedType, response.data[0].id);
                } else {
                    // Show a list to the user if multiple results are found
                    const adaptedResults = response.data.map(r => ({
                        ...r,
                        lon: r.lng,
                        display_name: r.formatted_address
                    }));
                    setSearchResults(adaptedResults);
                    setShowSelectionModal(true);
                }
            } else {
                setError('Endereço ou local não encontrado.');
            }
        } catch (err) {
            if (searchId !== activeSearchRef.current) return;
            console.error("Search error:", err);
            setError('Erro ao buscar o endereço/prédio. Tente novamente.');
        } finally {
            if (searchId === activeSearchRef.current) setIsGeocoding(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setError(null);
        setWarning(null);

        if (!input.trim()) {
            setError('Por favor, digite um endereço ou coordenadas.');
            return;
        }

        // The previous coverage belongs to a different query. Clear it before
        // opening the type picker or waiting for either search provider.
        onSearchStart?.();

        const parsed = parseInput(input);

        // If parsed is INVALID, treat it as a generic text search (address or name) instead of immediately failing
        const isCoordinate = parsed.type === 'COORDINATE';
        const searchValue = isCoordinate ? parsed.value : input.trim();

        if (isCoordinate) {
            onSearch(searchValue, null, input, 'street'); // Pass the original input as address/search term
        } else {
            // Check if input strongly looks like an address using regex
            // Brazilian style: street-type word anywhere in the string (e.g. "Rua Amando de Barros, 1500, Centro, Botucatu")
            const addressRegex = /\b(rua|r\.|avenida|av\.|av|travessa|trav\.|rodovia|rod\.|alameda|al\.|estrada|estr\.|praça|praca|pça|pca\.)(?:\s|$)/i;

            // American style: "Número Rua, Bairro (opcional), Cidade - Estado (opcional)"
            // e.g. "1500 Amando de Barros, Centro, Botucatu - SP" or "1500 R. Amando de Barros, Botucatu"
            // A leading house number followed by the street name and at least one comma-separated
            // segment (neighborhood/city) is a strong enough signal to treat this as an address search.
            const americanAddressRegex = /^\s*\d+[a-zA-Z]?\s+\S+.*,\s*\S+/;

            const isDefiniteAddress = addressRegex.test(searchValue) || americanAddressRegex.test(searchValue);

            // Care with "Ruas" which is a surname, however the regex avoids matching "Ruas" because of word boundary / plural checks (the regex matches "rua " or "rua\.").

            if (isDefiniteAddress) {
                // Clearly an address, search directly
                performSearch(searchValue, 'street');
            } else {
                // Ambiguous, ask the user
                setPendingSearchValue(searchValue);
                setShowTypeModal(true);
            }
        }
    };

    const handleSelectType = (type) => {
        setShowTypeModal(false);
        if (pendingSearchValue) {
            performSearch(pendingSearchValue, type);
        }
    };

    const handleSelectResult = (result) => {
        setShowSelectionModal(false);
        setWarning(result.approximate
            ? 'Localização aproximada da rua: o número informado não foi confirmado. Confira a posição no mapa antes de concluir a cobertura.'
            : result.source === 'nominatim' ? 'Busca aproximada. Confira a posição no mapa.' : null);
        const resolvedType = result.source === 'ozmap_region' ? 'condo' : selectedLocationType;
        const coverageSearchText = resolvedType === 'street'
            ? (result.display_name || pendingSearchValue || input)
            : (pendingSearchValue || input);
        onSearch({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) }, null, coverageSearchText, resolvedType, result.id);
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                <Search className="w-5 h-5 mr-2 text-blue-600" />
                Verificar Cobertura
            </h2>


            <form onSubmit={handleSearch} className="relative">

                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg leading-5 bg-white dark:bg-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-slate-900 dark:text-slate-100 transition-colors"
                            placeholder="Digite o endereço, coordenadas ou nome do local"
                            value={input}
                            onChange={(e) => {
                                // Invalidate any in-flight lookup as soon as the user starts
                                // entering a different location.
                                activeSearchRef.current += 1;
                                setInput(e.target.value);
                                onSearchStart?.();
                                setSearchResults([]);
                                setShowSelectionModal(false);
                                setError(null);
                                setWarning(null);
                                setIsGeocoding(false);
                            }}
                            aria-label="Endereço, coordenadas ou nome para verificar cobertura"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading || isGeocoding}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading || isGeocoding ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Navigation className="w-5 h-5" />
                        )}
                        <span className="hidden sm:inline ml-2">Verificar</span>
                    </button>
                </div>
                {error && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center animate-fadeIn">
                        {error}
                    </p>
                )}
                {warning && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center animate-fadeIn">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {warning}
                    </p>
                )}
            </form>

            <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                <p>Formatos suportados:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Endereço completo (Rua, Número, Cidade)</li>
                    <li>Endereço no formato Número, Rua, Cidade - UF (ex: 1500 Amando de Barros, Botucatu - SP)</li>
                    <li>Nome do condomínio/prédio</li>
                    <li>Coordenadas Decimais (-23.55, -46.63)</li>
                    <li>Coordenadas GMS (23° 33' 01" S, 46° 38' 02" W)</li>
                </ul>
            </div>

            {/* Type Selection Modal */}
            {showTypeModal && createPortal(
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Tipo de Busca</h3>
                            <button
                                onClick={() => setShowTypeModal(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                title="Fechar"
                                aria-label="Fechar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-slate-600 dark:text-slate-300 mb-6 text-center">
                                Você está buscando pelo <strong>nome do local</strong> (condomínio, prédio) ou pelo <strong>endereço</strong> (rua)?
                            </p>
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => handleSelectType('building')}
                                    className="flex items-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                >
                                    <div className="bg-blue-100 dark:bg-blue-900/50 p-3 rounded-full mr-4">
                                        <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-slate-800 dark:text-slate-100">Nome do Local</h4>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Buscar por Condomínios ou Prédios</p>
                                    </div>
                                </button>
                                <button
                                    onClick={() => handleSelectType('street')}
                                    className="flex items-center p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                                >
                                    <div className="bg-emerald-100 dark:bg-emerald-900/50 p-3 rounded-full mr-4">
                                        <Map className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-slate-800 dark:text-slate-100">Endereço</h4>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Buscar por Rua e Número</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Multiple Results Selection Modal */}
            {showSelectionModal && searchResults.length > 0 && createPortal(
                <div role="dialog" aria-modal="true" aria-label="Selecione o Local Correto" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Selecione o Local Correto</h3>
                            <button
                                onClick={() => setShowSelectionModal(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                title="Fechar"
                                aria-label="Fechar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                                Confira a rua, o bairro e a cidade e escolha o local correto para verificar a cobertura:
                            </p>
                            <div className="space-y-2">
                                {searchResults.map((result, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSelectResult(result)}
                                        className="w-full text-left p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                    >
                                        <MapPin className="w-5 h-5 text-slate-400 mr-3 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-slate-700 dark:text-slate-300">
                                            <span className="block font-medium">{result.street || result.display_name}</span>
                                            {selectedLocationType === 'street' && (
                                                <span className="block mt-1">Bairro: {result.neighborhood || 'não informado pelo serviço'} · {result.city || 'Cidade não informada'}</span>
                                            )}
                                            {result.street && <span className="block mt-1 text-xs">{result.display_name}</span>}
                                            {result.approximate && <span className="block mt-1 text-xs text-amber-700 dark:text-amber-300">Localização aproximada da rua; número não confirmado. Confira a posição no mapa.</span>}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CoverageSearch;
